// src/app/AccountNew/Team/validationSchemas.js

import * as Yup from "yup";
import { isValidEmail, isValidPhone } from 'utils/validation';
import { getPasswordSchema } from "../../../components/PasswordStrengthIndecator/utils";

export const chooseTierDefaultValue = {type: ""}
export const chooseCreateTeamDefaultValue = {firstName: "" ,lastName: "",phone:"",username:"",confirmEmail:"",newPwd:"",confirmPassword: "",spendingLimit:"0",propertyLimit:"0",profilePicture:[],securityDomainId:"1",pushToBatchDialer: false, allowClickToDial: false, allowInvestorOutreach: false}
export const step1ValidationSchema = Yup.object().shape({
  type: Yup.string().required('You must select a tier before continuing.')
});

export const profileValidationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  phone: Yup.string().required("Phone Number is required.") // Check empty
  .test(
    "is-valid-phone",
    "Phone Number must be exactly 10 digits.",
    (value) => {
      if (!value) return true; // Skip test if empty
      const digitsOnly = value.replace(/\D/g, "");
      return digitsOnly.length === 10;
    }
  ),
  username: Yup.string()
    .required("Email is required.")
    .max(254, "Email must be at most 254 characters.")
    .test('email-format', "Email is invalid.", function(value) {
      if (!value) return true;
      const emailRegex = /^(?![.])[a-zA-Z0-9._%+-]+(?<![.])@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(value);
    })
    .email("Email is invalid."),

  confirmEmail: Yup.string()
    .required("Confirm Email is required.")
    .max(254, "Email must be at most 254 characters.")
    .email("Confirm Email is invalid.")
    .when("username", {
      is: (username) => !!username, // only validate if username is not empty
      then: (schema) =>
        schema
          .required("Confirm Email is invalid.")
          .oneOf([Yup.ref("username")], "Emails must match"),
      otherwise: (schema) => schema.notRequired(),
    }),
 newPwd: getPasswordSchema("", "", "", "username", "firstName", "lastName"),
  confirmPassword: Yup.string()
    .required("Confirm New Password is required")
    .test("passwords-match", "Passwords Do Not Match", function(value) {
      const { newPwd } = this.parent;
      if (!newPwd && !value) return true; // If both are empty, it's valid (no change)
      if(!value) return true; // If confirm password is empty, skip this test (handled by required test)
      return value === newPwd; // Otherwise, they must match
    }),
    spendingLimit: Yup.string(),
    propertyLimit: Yup.string()
});

export const editProfileValidationSchema = (isBlankTM = false) => Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  phone: Yup.string().required("Phone Number is required.")
  .test(
    "is-valid-phone",
    "Phone Number must be exactly 10 digits.",
    (value) => {
      if (!value) return true;
      const digitsOnly = value.replace(/\D/g, "");
      return digitsOnly.length === 10;
    }
  ),
  username: Yup.string()
    .max(254, "Email must be at most 254 characters.")
    .matches(
    /^(?![.])[a-zA-Z0-9._%+-]+(?<![.])@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    "Email is invalid."
    )
    .email("Email is invalid."),
    confirmEmail: Yup.string()
    .max(254, "Email must be at most 254 characters.")
    .email("Confirm Email is invalid.")
    .matches(
    /^(?![.])[a-zA-Z0-9._%+-]+(?<![.])@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    "Confirm Email is invalid."
    )
    .when("username", (username, schema) => {
      return schema.oneOf([Yup.ref("username")], "Emails must match");
    }),
    newPwd: isBlankTM
      ? getPasswordSchema("", "", "", "username", "firstName", "lastName", true)
          .when("username", {
            is: (val) => !!val,
            then: (schema) => schema.required("Password is required when setting an email for the first time."),
            otherwise: (schema) => schema.nullable(),
          })
          .test('password-confirm', 'Password required when confirm new password is filled', function(value) {
            const { confirmPassword } = this.parent;
            if (!value && confirmPassword) return false;
            return true;
          })
      : getPasswordSchema("", "", "", "username", "firstName", "lastName", true)
          .test('password-confirm', 'Password required when confirm new password is filled', function(value) {
            const { confirmPassword } = this.parent;
            if (!value && confirmPassword) return false;
            return true;
          }),
    confirmPassword: Yup.string()
      .test("passwords-match", "Passwords Do Not Match", function(value) {
        const { newPwd } = this.parent;
        if (!newPwd && !value) return true;
        if(!value) return true;
        return value === newPwd;
      })
      .test('confirm-password-required', 'Confirm New Password is required', function(value) {
        const { newPwd } = this.parent;
        if (newPwd && !value) return false;
        return true;
      }),
    spendingLimit: Yup.number(),
    propertyLimit: Yup.number(),
  });


export const reviewTeamValidationSchema = Yup.object().shape({
  agreeToTerms: Yup.boolean().default(false).required("You must agree to the terms"),
});


export const getFullDataURL = (base64String) => {
  const binaryString = atob(base64String);
  const bytes = Array.from(binaryString).map(c => c.charCodeAt(0));

  // Extract first 12 bytes for comprehensive format matching
  const hexBytes = bytes
      .slice(0, 12)
      .map(b => b.toString(16).padStart(2, "0"))
      .join(" ");

  let mimeType = "";

  // PNG
  if (hexBytes.startsWith("89 50 4e 47")) {
      mimeType = "image/png";

  // JPEG
  } else if (hexBytes.startsWith("ff d8 ff")) {
      mimeType = "image/jpeg";

  } else if (
      binaryString.startsWith("RIFF") &&
      binaryString.slice(8, 12) === "WEBP"
  ) {
      mimeType = "image/webp";

  // HEIF/HEIC (brand `heic` or `heif` in ftyp box at offset 8)
  } else if (binaryString.trimStart().startsWith("<svg")) {
      mimeType = "image/svg+xml";

  } else {
      throw new Error("Unknown or unsupported image format");
  }

  return `data:${mimeType};base64,${base64String}`;
};
